import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type IntelKind =
  | 'risk' | 'issue' | 'assumption' | 'dependency' | 'stakeholder' | 'team_member'
  | 'change_request' | 'contract' | 'lesson' | 'metric';

export interface IntelItem {
  id: string;
  kind: IntelKind;
  item_key: string;
  payload: Record<string, unknown>;
  status: 'suggested' | 'approved' | 'rejected';
}

export interface RegisterEntry {
  id: string;
  entry_type: 'risk' | 'issue' | 'assumption';
  ref: string | null;
  title: string;
  category: string | null;
  probability: number | null;
  impact: number | null;
  score: number | null;
  rating: string | null;
  severity: string | null;
  validity: string | null;
  owner: string | null;
  status: string | null;
  mitigation: string | null;
  intel_item_id: string | null;
}

/** Kinds that approve_project_intel writes to project_risks. Everything else waits for its own tab. */
export const REGISTER_KINDS: IntelKind[] = ['risk', 'issue', 'assumption'];

/**
 * A project's risk register and the register facts detected in its documents.
 * Facts hang off the dossier (the project's document set); document_candidates
 * links a dossier to the project it created.
 */
export function useProjectIntel(projectId: string, onChanged?: () => void) {
  const [dossierKey, setDossierKey] = useState<string | null>(null);
  const [items, setItems] = useState<IntelItem[]>([]);
  const [register, setRegister] = useState<RegisterEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [candRes, regRes] = await Promise.all([
      supabase.from('document_candidates').select('dossier_key').eq('matched_project_id', projectId).not('dossier_key', 'is', null).limit(1),
      supabase
        .from('project_risks')
        .select('id, entry_type, ref, title, category, probability, impact, score, rating, severity, validity, owner, status, mitigation, intel_item_id')
        .eq('project_id', projectId)
        .order('score', { ascending: false, nullsFirst: false })
        .order('title'),
    ]);
    const key = (candRes.data?.[0]?.dossier_key as string | null | undefined) ?? null;
    setDossierKey(key);
    setRegister((regRes.data ?? []) as RegisterEntry[]);
    if (key) {
      const { data } = await supabase
        .from('project_intel_items')
        .select('id, kind, item_key, payload, status')
        .eq('dossier_key', key)
        .order('created_at')
        .range(0, 1999);
      setItems(((data ?? []) as unknown) as IntelItem[]);
    } else {
      setItems([]);
    }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { void load(); }, [load]);

  const finish = useCallback(async () => {
    await load();
    onChanged?.();
  }, [load, onChanged]);

  /** Approve every suggested register fact of the project except the ids the reviewer unticked. */
  const approve = useCallback(async (excludedIds: string[]) => {
    if (!dossierKey) return { ok: false as const, error: 'No documents are linked to this project' };
    setBusy(true);
    setError(null);
    const { data, error: rpcError } = await supabase.rpc('approve_project_intel', {
      p_project_id: projectId,
      p_dossier_key: dossierKey,
      p_reject: excludedIds,
    });
    setBusy(false);
    if (rpcError) { setError(rpcError.message); return { ok: false as const, error: rpcError.message }; }
    // Refresh signals from the new register entries. Best effort: roles outside admin/PMO/executive are refused, which is fine.
    void supabase.functions.invoke('generate-signals', { body: { action: 'scan' } });
    await finish();
    return { ok: true as const, result: data as { approved: number; rejected: number; remaining: number } };
  }, [dossierKey, projectId, finish]);

  const undo = useCallback(async () => {
    if (!dossierKey) return { ok: false as const, error: 'No documents are linked to this project' };
    setBusy(true);
    setError(null);
    const { data, error: rpcError } = await supabase.rpc('undo_project_intel', { p_dossier_key: dossierKey });
    setBusy(false);
    if (rpcError) { setError(rpcError.message); return { ok: false as const, error: rpcError.message }; }
    void supabase.functions.invoke('generate-signals', { body: { action: 'scan' } });
    await finish();
    return { ok: true as const, result: data as { reverted: number; skipped: Array<{ id: string; title: string }> } };
  }, [dossierKey, finish]);

  return { dossierKey, items, register, loading, busy, error, approve, undo, reload: load };
}
